import { useAppDispatch, useAppSelector } from "hooks/redux";
import { renderFormBuilderItemContent } from "pages/secure/facility/websiteBuilder/editor/formBuilder/editor/designSpace/views/components/item/renderFormBuilderItemContent";
import { formBuilderGenerateFormDesignStyles, IFormBuilderForm } from "pages/secure/facility/websiteBuilder/editor/formBuilder/editor/model/formBuilderForm";
import { formBuilderGenerateRowDesignStyles } from "pages/secure/facility/websiteBuilder/editor/formBuilder/editor/model/formBuilderRow";
import { formBuilderGenerateItemDesignStyles } from "pages/secure/facility/websiteBuilder/editor/formBuilder/editor/model/item/formBuilderItem";
import { IWebsiteBuilderForm, websiteBuilderGenerateFormDesignStyles } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderForm";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { websiteBuilderUpdateContainerSettings, websiteBuilderUpdateSelectedNode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { FormBuilder } from "pages/secure/facility/websiteBuilder/editor/formBuilder/FormBuilder";

interface IWebsiteBuilderFormViewProps {
  node: IWebsiteBuilderForm;
}

export function WebsiteBuilderFormView(props: IWebsiteBuilderFormViewProps) {
  const { node } = props;

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const forms = useAppSelector(store => store.websiteBuilderStore.editor.forms);
  const selectedNode = useAppSelector(store => store.websiteBuilderStore.editor.selectedNode);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const settings = node.container.settings;
  const designStyles = websiteBuilderGenerateFormDesignStyles();

  const formId = settings.formId;

  const selected = selectedNode?.id === node.id;
  const builderActive = selected && selectedNode?.editingMode?.type === "container-builder";

  function renderForm(form: IFormBuilderForm) {
    const formDesignStyles = formBuilderGenerateFormDesignStyles();

    return (
      <div
        className={formDesignStyles.className}
        style={formDesignStyles.styleProperties}
      >
        {form.children.map(row => {
          const rowDesignStyles = formBuilderGenerateRowDesignStyles(row);

          return (
            <div
              key={row.id}
              className={rowDesignStyles.className}
              style={rowDesignStyles.styleProperties}
            >
              {row.children.map(item => {
                const itemDesignStyles = formBuilderGenerateItemDesignStyles(item);

                return (
                  <div
                    key={item.id}
                    className={itemDesignStyles.className}
                    style={itemDesignStyles.styleProperties}
                  >
                    {renderFormBuilderItemContent(item)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  const content = useMemo(() => {
    if (formId == null) {
      return (
        <div className="editor-form-instruction">
          <h3>{t("Add a form")}</h3>
          <p>{t("Double-click to choose an existing form or create a new one.")}</p>
        </div>
      );
    } else {
      const form = forms.find(form => form.id === formId);

      if (form == null) {
        return (
          <div className="editor-form-instruction">
            <h3>{t("Form not found")}</h3>
          </div>
        );
      } else {
        return renderForm(form);
      }
    }
  }, [formId, forms]);
  
  function dispatchSettingsFormId(formId: number) {
    if (formId === settings.formId) {
      return;
    }

    const prevSettings = cloneDeep(settings);
    const nextSettings = cloneDeep(settings);

    nextSettings.formId = formId;
    
    dispatch(
      websiteBuilderUpdateContainerSettings(activePageId, deviceView, node.id, {
        id: node.id,
        prevContainerSettings: {
          type: "form",
          settings: prevSettings,
        },
        nextContainerSettings: {
          type: "form",
          settings: nextSettings,
        },
      }),
    );
  }

  function closeFormBuilder() {
    dispatch(
      websiteBuilderUpdateSelectedNode({
        selectedNodeUpdates: {
          type: selectedNode.type,
          editingMode: {
            type: "none",
          },
        },
      }),
    );
  }

  return (
    <div
      className={`${designStyles.className} editor-form`}
      style={designStyles.styleProperties}
    >
      {content}

      {builderActive && (
        <FormBuilder
          selectedFormId={settings.formId}
          setSelectedFormId={dispatchSettingsFormId}
          closeFormBuilder={closeFormBuilder}
        />
      )}
    </div>
  );
}
